import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FormProvider, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import {
  ActionLayer,
  Spacer,
  useMediaQuery,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { StoreListFieldInformationLayer } from 'shared/components/StoreListSection/components';
import { useQuery } from 'shared/hooks';
import PDPInfoLayer from '../PDPInfoLayer';
import ShopAddressChecker from '../ShopAddressChecker';
import StoreListForOneOfferIdField from '../StoreListForOneOfferIdField';

interface AddToCartButtonProps {
  showFindStore: boolean;
  handleHideFindStore: () => void;
}

interface GetContainerVariantsProps {
  isBig: boolean;
  isLil: boolean;
}

const bigFindStoreLayerWidth = 456;
const slimFindStoreWidth = 400;

const animationLil = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, display: 'block' },
};

const animationSlim = {
  hidden: { x: 0, opacity: 0 },
  visible: { x: slimFindStoreWidth, opacity: 1, display: 'block' },
};

const animationBig = {
  hidden: { x: 0, opacity: 0 },
  visible: { x: bigFindStoreLayerWidth, opacity: 1, display: 'block' },
};

const AnimatedContainer = styled(motion.div)`
  ${({ theme: { media, zIndex } }) => css`
    display: none;
    height: 100vh;
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    z-index: ${zIndex.overlay};
    ${media.down('lil')} {
      z-index: ${zIndex.modal};
    }
  `}
`;

const getContainerVariants = ({ isBig, isLil }: GetContainerVariantsProps) => {
  if (isBig) {
    return animationBig;
  }
  if (isLil) {
    return animationLil;
  }
  return animationSlim;
};

const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();
};
const FindStoreLayer = ({
  showFindStore,
  handleHideFindStore,
}: AddToCartButtonProps) => {
  const form = useForm({
    defaultValues: {},
    mode: 'all',
  });
  const [isInfoLayerOpen, setInfoLayerOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const { parseQueryValueString } = useQuery();
  const isBig = useMediaQuery({ min: 'big' });
  const isLil = useMediaQuery({ max: 'lil' });
  const containerVariants = getContainerVariants({ isBig, isLil });
  const infoLayerContentKey = parseQueryValueString('info');
  const animate =
    isInfoLayerOpen || Boolean(infoLayerContentKey) ? 'visible' : 'hidden';

  useEffect(() => {
    if (isInfoLayerOpen) {
      trackCustomLink(`${selectedStore.displayName} More details`);
    }
  }, [isInfoLayerOpen, selectedStore]);

  return (
    <LinkRegion regionId="delivery.findYourNearestStore">
      <ActionLayer
        isOpen={showFindStore}
        title="Find your nearest store"
        onClose={() => {
          setInfoLayerOpen(false);
          handleHideFindStore();
        }}
        status="default"
      >
        <form onSubmit={handleSubmit}>
          <FormProvider {...form}>
            <Spacer variant="spacing-06" />
            <ShopAddressChecker />
            <Spacer variant="spacing-02" />
            <StoreListForOneOfferIdField
              setSelectedStore={setSelectedStore}
              setInfoLayerOpen={setInfoLayerOpen}
            />
          </FormProvider>
        </form>
      </ActionLayer>
      <AnimatedContainer
        variants={containerVariants}
        initial="hidden"
        animate={animate}
        transition={{
          delay: 0.1,
          duration: 0.8,
          ease: [0.39, 0.62, 0.13, 1],
          type: 'tween',
        }}
      >
        <StoreListFieldInformationLayer
          selectedStore={selectedStore}
          setOpen={setInfoLayerOpen}
          isOpen={isInfoLayerOpen}
        />
        <PDPInfoLayer />
      </AnimatedContainer>
    </LinkRegion>
  );
};

export default FindStoreLayer;
